/**
 * Helpers for salt composition + formulation coverage matching.
 *
 * Nitrogen semantics (strict — never collapse forms):
 * - N_NO3 / NO3  = nitrate-N only
 * - N_NH4 / NH4  = ammonium-N only
 * - N            = total nitrogen = NH4-N + NO3-N (never “means” only one form)
 */

/**
 * Canonical symbol for storage/matching.
 * Does NOT map plain N → N_NO3 or N_NH4.
 */
export function normalizeElementSymbol(symbol: string | undefined | null): string {
  if (!symbol) return '';
  const s = String(symbol).trim();
  const u = s.toUpperCase();

  // Nitrate-N only
  if (
    s === 'N_NO3' ||
    s === 'N-NO3' ||
    u === 'NNO3' ||
    u === 'NO3' ||
    s === 'N (NO3-)' ||
    s === 'N (NO₃⁻)'
  ) {
    return 'N_NO3';
  }

  // Ammonium-N only
  if (
    s === 'N_NH4' ||
    s === 'N-NH4' ||
    u === 'NNH4' ||
    u === 'NH4' ||
    s === 'N (NH4+)' ||
    s === 'N (NH₄⁺)'
  ) {
    return 'N_NH4';
  }

  // Total nitrogen only (NH4 + NO3). Never treat as a single ion form.
  if (u === 'N' || s === 'N total' || s === 'N_TOTAL') {
    return 'N';
  }

  if (s === 'P2O5' || s === 'PO4') return 'P';
  if (s === 'K2O') return 'K';
  if (s === 'SO4') return 'S';
  return s;
}

export function isNitrateN(symbol: string | undefined | null): boolean {
  return normalizeElementSymbol(symbol) === 'N_NO3';
}

export function isAmmoniumN(symbol: string | undefined | null): boolean {
  return normalizeElementSymbol(symbol) === 'N_NH4';
}

export function isTotalN(symbol: string | undefined | null): boolean {
  return normalizeElementSymbol(symbol) === 'N';
}

export function isAnyNitrogen(symbol: string | undefined | null): boolean {
  const n = normalizeElementSymbol(symbol);
  return n === 'N' || n === 'N_NO3' || n === 'N_NH4';
}

/**
 * Exact chemical channel match for matrix / ppm attribution.
 * N_NO3 ≠ N_NH4 ≠ N (plain total).
 */
export function elementsMatch(
  a: string | undefined | null,
  b: string | undefined | null
): boolean {
  const na = normalizeElementSymbol(a);
  const nb = normalizeElementSymbol(b);
  if (!na || !nb) return false;
  return na === nb;
}

/**
 * Coverage UI: does this salt channel help meet a formulation target?
 * - Exact form match always
 * - Plain target N is covered only by N, N_NO3, or N_NH4 (parts of total N)
 * - Target N_NO3 is NOT covered by N_NH4 alone (and vice versa)
 * - Salt plain N covers target N only (not a specific form target)
 */
export function saltCoversTarget(
  saltSymbol: string | undefined | null,
  targetSymbol: string | undefined | null
): boolean {
  const salt = normalizeElementSymbol(saltSymbol);
  const target = normalizeElementSymbol(targetSymbol);
  if (!salt || !target) return false;
  if (salt === target) return true;
  // Total-N target can be met by either form or total-N salt %
  if (target === 'N' && (salt === 'N_NO3' || salt === 'N_NH4' || salt === 'N')) {
    return true;
  }
  return false;
}

export type FlatSaltElement = {
  symbol: string;
  normalized: string;
  percentage: number;
  rawSymbol: string;
};

/**
 * Infer ionic N form from fertilizer identity when DB only stores plain "N".
 * Does NOT invent a split when both NH4 and NO3 are present in the product name/formula.
 */
export function inferNitrogenFormFromProduct(salt: {
  name?: string;
  formula?: string;
}): 'N_NO3' | 'N_NH4' | null {
  const name = String(salt.name || '').toLowerCase();
  const formula = String(salt.formula || '').toUpperCase().replace(/\s/g, '');

  const hasNh4 =
    formula.includes('NH4') ||
    name.includes('ammonium') ||
    name.includes('monoammonium') ||
    name.includes('diammonium') ||
    /\bmap\b/.test(name) ||
    /\bdap\b/.test(name) ||
    name.includes('ammonia');

  const hasNo3 =
    formula.includes('NO3') ||
    name.includes('nitrate') ||
    name.includes('nitric');

  // Mixed products (e.g. some CN with NH4) must declare forms explicitly
  if (hasNh4 && hasNo3) return null;
  if (hasNh4) return 'N_NH4';
  if (hasNo3) return 'N_NO3';
  return null;
}

/**
 * Extract composition for calculations.
 * Plain "N" is reclassified to N_NO3 or N_NH4 when product identity is unambiguous.
 * If the salt already has N_NO3/N_NH4, a plain N row is dropped (avoids double-count).
 */
export function getSaltComposition(salt: {
  elements?: any[];
  name?: string;
  formula?: string;
}): FlatSaltElement[] {
  if (!Array.isArray(salt.elements) || salt.elements.length === 0) return [];

  const rawRows = salt.elements
    .map((el) => {
      const raw =
        el?.element?.symbol ||
        el?.symbol ||
        el?.elementSymbol ||
        '';
      const percentage = Number(el?.percentage ?? el?.percent ?? 0);
      if (!raw || !(percentage > 0)) return null;
      return {
        rawSymbol: raw,
        symbol: raw,
        normalized: normalizeElementSymbol(raw),
        percentage,
      };
    })
    .filter((x): x is FlatSaltElement => x != null);

  const hasNo3 = rawRows.some((r) => r.normalized === 'N_NO3');
  const hasNh4 = rawRows.some((r) => r.normalized === 'N_NH4');
  const inferred = inferNitrogenFormFromProduct(salt);

  return rawRows
    .map((r) => {
      if (r.normalized !== 'N') return r;

      // Already have form-specific N on this product → plain N would double-count
      if (hasNo3 || hasNh4) {
        return null;
      }

      if (inferred === 'N_NO3') {
        return {
          ...r,
          symbol: 'N_NO3',
          normalized: 'N_NO3',
        };
      }
      if (inferred === 'N_NH4') {
        return {
          ...r,
          symbol: 'N_NH4',
          normalized: 'N_NH4',
        };
      }
      // Unknown product: keep plain N (verification will WARNING, not invent form)
      return r;
    })
    .filter((x): x is FlatSaltElement => x != null);
}

export function saltHasComposition(salt: { elements?: any[] }): boolean {
  return getSaltComposition(salt).length > 0;
}

export function getFormulationTargets(formulation: any): {
  symbol: string;
  normalized: string;
  concentration?: number;
}[] {
  const list = formulation?.elements || formulation?.targetElements || [];
  if (!Array.isArray(list)) return [];

  return list
    .map((e: any) => {
      const raw =
        e?.element?.symbol ||
        e?.symbol ||
        e?.elementSymbol ||
        '';
      if (!raw) return null;
      return {
        symbol: raw,
        normalized: normalizeElementSymbol(raw),
        concentration: Number(e?.concentration ?? e?.targetConcentration ?? e?.target ?? NaN),
      };
    })
    .filter((x: any) => x != null);
}
